import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { buffer, debounceTime, filter } from 'rxjs/operators';
import { DraftDocumentKind } from 'src/app/models/e-invoice/draft-merge';
import {
  EInvoiceAnswerCode,
  EInvoiceDocumentStatus,
  EInvoiceDocumentType,
} from 'src/app/models/enums/e-invoice-enums';
import { SignalRService } from './signalr.service';

/** Sunucudan gelen tek bir durum değişikliği (EInvoiceStatusNotification). */
export interface EInvoiceStatusChange {
  /** Yerel belge kimliği; ekran satırı bununla bulunur. */
  id: number;
  documentNumber?: string | null;
  status: EInvoiceDocumentStatus;
  previousStatus: EInvoiceDocumentStatus;
  statusDetail?: string | null;
  answerCode?: EInvoiceAnswerCode | null;
  gibStatusCode?: string | null;
  documentType: EInvoiceDocumentType;
}

/** Yeni oluşan belge (EInvoiceDocumentCreatedNotification). */
export interface EInvoiceDocumentCreated {
  /** Gider pusulası mı fatura mı — taslak birleştirmedeki tiple aynı sayılar. */
  kind: DraftDocumentKind;
  id: number;
  number?: string | null;
  customerName?: string | null;
  amount: number;
  currencyCode?: string | null;
  /** EInvoiceDocumentStatus ya da ExpenseVoucherStatus; çoğunlukla taslak. */
  status: number;
  /** Yalnızca faturada dolu. */
  documentType?: EInvoiceDocumentType | null;
  issueDate: string;
}

/**
 * E-belge durum değişikliklerinin canlı akışı.
 *
 * Arka uçtaki periyodik durum sorgulama servisi (EInvoiceStatusSync) bir turda
 * sonuçlanan belgeleri bu hub'dan yayınlıyor; ekranlar sayfayı yenilemeden
 * güncelleniyor.
 *
 * <b>Değişiklikler tur bazında, tek tek değil yayılıyor:</b> bir turda elli
 * belge birden sonuçlanabiliyor. Dinleyen ekran diziyi bir kez işleyip özetini
 * bir kez tazeliyor; belge başına olay yayılsaydı aynı iş elli kez yapılırdı.
 *
 * Bağlantı ihtiyaç duyan ekranda açılıyor (muhasebe dışındaki kullanıcı için
 * boşuna soket tutmanın anlamı yok); <see cref="SignalRService"/> aynı hub'a
 * ikinci bir bağlantı açmıyor.
 */
@Injectable({ providedIn: 'root' })
export class EInvoiceStatusHubService {
  private readonly hubUrl = '/eInvoiceHub';
  private readonly changes = new Subject<EInvoiceStatusChange[]>();
  private readonly created = new Subject<EInvoiceDocumentCreated>();

  /** Bir turda değişen belgeler. */
  readonly statusChanged$: Observable<EInvoiceStatusChange[]> = this.changes.asObservable();

  /**
   * Yeni oluşan belgeler, öbek hâlinde.
   *
   * Sunucu belge başına tek mesaj gönderiyor; banka hareketlerinden toplu
   * taslak üreten tur bir seferde onlarca belge yazabiliyor. Ham akış ekrana
   * bağlansaydı liste her belge için yeniden yüklenirdi. Sessizliğe göre
   * tamponlanıyor: seri biter bitmez tek bir dizi geliyor, boşta zamanlayıcı
   * çalışmıyor (aralıklı tamponun aksine).
   */
  readonly documentCreated$: Observable<EInvoiceDocumentCreated[]> = this.created.pipe(
    buffer(this.created.pipe(debounceTime(EInvoiceStatusHubService.CreatedBurstMs))),
    filter(batch => batch.length > 0),
  );

  /** Seri oluşturmanın bittiğine karar vermek için beklenen sessizlik. */
  private static readonly CreatedBurstMs = 800;

  /**
   * Dinleyicinin TEK sahibi burasıdır. Bayrak olmasa iki ekran birden açıkken
   * aynı bağlantıya iki handler bağlanır ve her değişiklik iki kez işlenirdi.
   */
  private registered = false;

  constructor(private signalR: SignalRService) {}

  connect(): void {
    if (this.registered) {
      return;
    }

    const connection = this.signalR.start(this.hubUrl);
    if (!connection) {
      // Token yoksa start() null döner; oturum açıldığında tekrar denenir.
      return;
    }

    this.registered = true;
    connection.on('receiveEInvoiceStatusChanged', (changes: EInvoiceStatusChange[]) => {
      if (Array.isArray(changes) && changes.length) {
        this.changes.next(changes);
      }
    });

    connection.on('receiveEInvoiceDocumentCreated', (document: EInvoiceDocumentCreated) => {
      if (document?.id) {
        this.created.next(document);
      }
    });
  }

  /** Çıkışta çağrılır; bayrak da sıfırlanır ki sonraki oturumda yeniden bağlanılsın. */
  disconnect(): void {
    this.registered = false;
    this.signalR.stop(this.hubUrl);
  }
}
